import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";

const links = [
  { label: "About", target: "#about" },
  { label: "Services", target: "#services" },
  { label: "How It Works", target: "#how-it-works" },
  { label: "Community", target: "#community" },
  { label: "Get Involved", target: "#ways-to-help" },
  { label: "Visit Us", target: "#contact" },
];

export default function Navbar({ scrollTo }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (target) => {
    setOpen(false);
    scrollTo(target);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        scrolled
          ? "border-b border-gold/20 bg-[#FAF7F1]/85 shadow-[0_8px_30px_-18px_rgba(22,19,13,0.35)] backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 sm:px-8">
        <button
          onClick={() => go("#top")}
          className="flex items-center gap-3"
          data-testid="nav-logo-btn"
          aria-label="The Hive — back to top"
        >
          <img
            src="/assets/hive-logo-transparent.png"
            alt="The Hive logo"
            className="h-14 w-auto object-contain sm:h-16"
          />
          <span className="flex flex-col justify-center leading-none">
            <span className="font-display text-2xl font-bold tracking-tight text-ink sm:text-[2rem]" data-testid="nav-wordmark">
              The Hive
            </span>
          </span>
        </button>

        <nav className="hidden items-center gap-7 lg:flex" data-testid="nav-links">
          {links.map((l) => (
            <button
              key={l.target}
              onClick={() => go(l.target)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="group relative text-sm font-semibold tracking-wide text-ink-soft transition-colors duration-300 hover:text-gold-deep"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-[2px] w-0 bg-gold transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <a
            href="tel:4794375939"
            data-testid="nav-call-btn"
            className="flex items-center gap-2 rounded-full border border-gold/50 px-4 py-2 text-sm font-bold text-gold-deep transition-colors duration-300 hover:bg-gold hover:text-white"
          >
            <Phone size={15} strokeWidth={2.5} />
            Call
          </a>
          <button
            onClick={() => go("#contact")}
            data-testid="nav-get-support-btn"
            className="rounded-full bg-ink px-5 py-2 text-sm font-bold text-cream transition-colors duration-300 hover:bg-gold-deep"
          >
            Get Support
          </button>
        </div>

        <button
          className="text-ink lg:hidden"
          onClick={() => setOpen(!open)}
          data-testid="nav-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-gold/20 bg-[#FAF7F1]/95 backdrop-blur-xl lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {links.map((l) => (
                <button
                  key={l.target}
                  onClick={() => go(l.target)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="py-2 text-left text-base font-semibold text-ink-soft"
                >
                  {l.label}
                </button>
              ))}
              <a
                href="tel:4794375939"
                data-testid="nav-mobile-call-btn"
                className="mt-2 flex items-center justify-center gap-2 rounded-full bg-gold px-4 py-3 text-sm font-bold text-white"
              >
                <Phone size={15} strokeWidth={2.5} /> Call
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
